import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { canEdit } from "../../lib/permissions";
import { Btn, Empty, Card } from "../../components/UI";
import { SlidePanel, ConfirmDialog, ReadOnlyChip } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { AllergiesPanel } from "../AllergiesPanel";
import {
  Plus, Pencil, Trash2, Stethoscope, ListChecks, Pill, ShieldAlert, FlaskConical,
  Users, Syringe, ArrowRightLeft, GraduationCap, FileCode2, Copy,
  CalendarPlus, Info, Archive, RotateCcw, Ban, Download, FileText,
} from "lucide-react";
import CodeAutocomplete from "./CodeAutocomplete";
import { DiagnosisPicker } from "../../components/DiagnosisPicker";
import { NewPill } from "../../components/NewPill";
import { downloadFile } from "../../lib/download";
import { ArchiveDiagnosisDialog, VoidDiagnosisDialog, DiagnosisHistoryPanel, DiagnosisListToggles, ClinicianSelect, PrincipalNote, DX_TYPES, DX_TYPE_LABELS, DX_TYPE_HINT, canRemoveDx } from "../../components/DiagnosisExtras";
import { PatientEducationPanel } from "../../components/education/PatientEducationPanel";
import { toast } from "sonner";

const fmt = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "-");
const chip = (color) => `rounded-full px-2 py-0.5 text-[10px] font-bold ${color}`;
const STATUS_TONE = {
  Active: "bg-[#E8F5E9] text-[#2E7D32]",
  "In Remission": "bg-[#FFF3E0] text-[#8D5A00]",
  Resolved: "bg-[#EFEBE0] text-[#6D4C41]",
  Inactive: "bg-[#EFEBE0] text-[#6D4C41]",
  Chronic: "bg-[#EDE7F6] text-[#5E35B1]",
  Archived: "bg-[#ECEFF1] text-[#546E7A]",
  Voided: "bg-[#FFEBEE] text-[#C62828]",
  Ordered: "bg-[#E3F2FD] text-[#1565C0]",
  Collected: "bg-[#FFF3E0] text-[#8D5A00]",
  "In Progress": "bg-[#FFF3E0] text-[#8D5A00]",
  Resulted: "bg-[#E8F5E9] text-[#2E7D32]",
  Cancelled: "bg-[#FFEBEE] text-[#C62828]",
  Pending: "bg-[#FFF3E0] text-[#8D5A00]",
  Scheduled: "bg-[#E3F2FD] text-[#1565C0]",
  Attended: "bg-[#E8F5E9] text-[#2E7D32]",
  "No-Show": "bg-[#FFEBEE] text-[#C62828]",
  Completed: "bg-[#E8F5E9] text-[#2E7D32]",
  Refused: "bg-[#FFEBEE] text-[#C62828]",
  Contraindicated: "bg-[#FFEBEE] text-[#C62828]",
  "Not Given": "bg-[#FFF3E0] text-[#8D5A00]",
  "Series In Progress": "bg-[#E3F2FD] text-[#1565C0]",
};
const PRIORITY_TONE = {
  Routine: "bg-[#EFEBE0] text-[#6D4C41]",
  Urgent: "bg-[#FFF3E0] text-[#8D5A00]",
  ASAP: "bg-[#FFF3E0] text-[#8D5A00]",
  STAT: "bg-[#FFEBEE] text-[#C62828]",
};

function useDict() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/medical/dictionaries").then((r) => setD(r.data)).catch(() => {}); }, []);
  return d;
}

function useList(url, deps = []) {
  const [items, setItems] = useState([]);
  const load = () => api.get(url).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, deps); // eslint-disable-line
  return { items, reload: load };
}

const IconBtn = ({ testId, label, onClick, danger, children }) => (
  <button type="button" data-testid={testId} aria-label={label} onClick={onClick}
    className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
      danger ? "text-[#C62828] hover:bg-[#FFEBEE]" : "text-[#7B6157] hover:bg-[#EDE7DC]"}`}>
    {children || (danger ? <Trash2 size={13} /> : <Pencil size={13} />)}
  </button>
);

function Toolbar({ title, count, onAdd, editable, addTestId, addLabel, extraAction, filterValue, onFilter }) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div>
        <h3 className="font-heading text-lg font-bold text-[#3E2723]">{title}</h3>
        <p className="text-xs text-[#7B6157]">{count} entr{count === 1 ? "y" : "ies"}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {onFilter && (
          <input data-testid="medical-filter-input" placeholder="Search…" value={filterValue}
            onChange={(e) => onFilter(e.target.value)}
            className="rounded-full border border-[#E5DFD3] bg-white px-3.5 py-1.5 text-xs outline-none w-44 focus:border-[#8D6E63]" />
        )}
        {extraAction}
        {editable && <Btn data-testid={addTestId} variant="outline" onClick={onAdd}><Plus size={14} /> {addLabel}</Btn>}
      </div>
    </div>
  );
}

/* ================== 1. Master Problem List ================== */
const DX_STATUSES = ["Active", "In Remission", "Resolved", "Inactive", "Chronic"];
const DX_EMPTY = { icd10_code: "", icd9_code: "", snomed_code: "", description: "", dx_type: "Principal",
                   status: "Active", date_diagnosed: "", onset_date: "", resolution_date: "", ruled_out_date: "",
                   diagnosed_by: "", diagnosed_by_id: "", catalog_id: "", verifier: "", severity: "", chronicity: "", certainty: "Confirmed",
                   body_site: "", symptoms: "", triggers: "", notes: "" };

function ProblemList({ client, editable, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [showArchived, setShowArchived] = useState(false);
  const [showVoided, setShowVoided] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const listQs = new URLSearchParams({ ...(showArchived ? { include_archived: "true" } : {}), ...(showVoided ? { include_voided: "true" } : {}) }).toString();
  const { items, reload } = useList(`/clients/${client.id}/diagnoses${listQs ? `?${listQs}` : ""}`, [client.id, listQs]);
  const [voiding, setVoiding] = useState(null);
  const changed = () => { reload(); onChanged?.(); };
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(DX_EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const [education, setEducation] = useState(null);
  const [hl7, setHl7] = useState(null);
  const [followMap, setFollowMap] = useState({});
  const [detailFollow, setDetailFollow] = useState(null);
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const codesLocked = !!editId && !canRemoveDx(user, form);

  useEffect(() => {
    api.get(`/clients/${client.id}/followup-suggestions`).then((r) => {
      const map = {};
      (r.data?.suggestions || []).forEach((s) => { if (s.suggestion) map[s.diagnosis_id] = s.suggestion; });
      setFollowMap(map);
    }).catch(() => {});
  }, [client.id, items.length]);

  const filtered = useMemo(() => items.filter((d) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [d.icd10_code, d.icd9_code, d.snomed_code, d.description, d.dx_type, d.status].some((x) => (x || "").toLowerCase().includes(s));
  }), [items, q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return toast.error("Description is required");
    const payload = { ...form, code: form.icd10_code || form.code };
    try {
      if (editId) await api.put(`/diagnoses/${editId}`, payload);
      else await api.post(`/clients/${client.id}/diagnoses`, payload);
      toast.success(editId ? "Problem updated" : "Problem added");
      setPanel(false); changed();
    } catch (err) { toast.error(apiError(err)); }
  };
  const restore = async (d) => {
    try { await api.post(`/diagnoses/${d.id}/restore`); toast.success("Diagnosis restored"); changed(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const openNew = () => {
    const mine = ["clinician", "admin"].includes(user?.role);
    setForm({ ...DX_EMPTY, date_diagnosed: dayjs().format("YYYY-MM-DD"),
      diagnosed_by: mine ? `${user.first_name} ${user.last_name}` : "", diagnosed_by_id: mine ? user.id : "" });
    setEditId(null); setPanel(true);
  };
  const exportDx = async (d, format) => {
    try { await downloadFile(`/diagnoses/${d.id}/public-health`, { format }, `public-health-${d.code || "diagnosis"}.${format}`); toast.success(`${format.toUpperCase()} downloaded`); }
    catch (err) { toast.error(err.message); }
  };
  const openHl7 = async (d) => {
    try { const r = await api.get(`/diagnoses/${d.id}/hl7`); setHl7({ ...r.data, dx: d }); }
    catch (err) { toast.error(apiError(err)); }
  };
  const scheduleFollowup = (d, sug) => {
    const params = new URLSearchParams({
      client_id: client.id,
      date: sug.suggested_date || "",
      type: sug.visit_type || "Medication Review",
      note: `Follow-up for ${d.icd10_code || d.code || ""} ${d.description}`.trim(),
    });
    navigate(`/scheduler?${params.toString()}`);
  };

  return (
    <div data-testid="problem-list-panel">
      <Toolbar title="All Master Problem List" count={filtered.length} editable={editable}
        addTestId="add-problem-btn" addLabel="Add New Master Problem" filterValue={q} onFilter={setQ}
        extraAction={<DiagnosisListToggles showArchived={showArchived} setShowArchived={setShowArchived} showVoided={showVoided} setShowVoided={setShowVoided} isAdmin={isAdmin} onHistory={() => setHistoryOpen(true)} testPrefix="problem" />}
        onAdd={openNew} />

      {filtered.length === 0 ? <Empty text="No problems on file" /> : (
        <div className="overflow-x-auto rounded-2xl border border-[#EFEBE0]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FDFCF8]">
              <tr className="text-left text-[10px] font-bold uppercase text-[#7B6157]">
                <th className="px-4 py-3">ICD-10</th>
                <th className="px-4 py-3">ICD-9</th>
                <th className="px-4 py-3">SNOMED</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Date Diagnosed</th>
                <th className="px-4 py-3">Date Modified</th>
                <th className="px-4 py-3">Date Ruled Out</th>
                <th className="px-4 py-3">Diagnosed By</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const sug = followMap[d.id];
                const weeks = sug ? Math.round(sug.days / 7) : 0;
                const humanInterval = sug ? (sug.days < 14 ? `${sug.days} days` :
                                              sug.days === 14 ? "2 weeks" :
                                              sug.days === 21 ? "3 weeks" :
                                              sug.days === 30 ? "30 days" :
                                              sug.days === 60 ? "2 months" :
                                              sug.days === 42 ? "6 weeks" :
                                              sug.days === 90 ? "3 months" :
                                              `${weeks} weeks`) : "";
                return (
                <tr key={d.id} data-testid={`problem-row-${d.id}`} className={`border-t border-[#EFEBE0] hover:bg-[#FDFCF8] ${d.status === "Archived" ? "opacity-60" : d.status === "Voided" ? "bg-[#FFF8F8]" : ""}`}>
                  <td className="px-4 py-2 font-mono text-[13px] text-[#3E2723]">{d.icd10_code || d.code || "-"}</td>
                  <td className="px-4 py-2 font-mono text-[13px] text-[#7B6157]">{d.icd9_code || "-"}</td>
                  <td className="px-4 py-2 font-mono text-[13px] text-[#7B6157]">{d.snomed_code || "-"}</td>
                  <td className="px-4 py-2 text-[#3E2723]">
                    <div>{d.description}</div>
                    <div className="text-[10px] text-[#84675B]" data-testid={`problem-type-${d.id}`}>{DX_TYPE_LABELS[d.dx_type] || d.dx_type}{d.catalog_id ? " · from library" : ""}</div>
                    {d.status === "Voided" && <div className="mt-0.5 text-[11px] text-[#C62828]" data-testid={`problem-void-info-${d.id}`}>Voided by {d.voided_by_name} on {fmt(d.voided_at)}: {d.void_reason}</div>}
                    {d.status === "Archived" && d.archive_reason && <div className="mt-0.5 text-[11px] text-[#546E7A]">Archived: {d.archive_reason}</div>}
                    {sug && (
                      <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`problem-followup-${d.id}`}>
                        <button type="button" onClick={() => setDetailFollow({ dx: d, sug })}
                          className="inline-flex items-center gap-1 rounded-full bg-[#EDE7DC] px-2 py-0.5 text-[11px] font-bold text-[#4E342E] hover:bg-[#E4D8C6]"
                          data-testid={`problem-followup-pill-${d.id}`}>
                          <CalendarPlus size={10} /> Follow-up in {humanInterval}
                        </button>
                        <span className="text-[10px] text-[#7B6157]">
                          {dayjs(sug.suggested_date).format("MMM D, YYYY")}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2"><span className={chip(STATUS_TONE[d.status] || STATUS_TONE.Active)}>{d.status || "Active"}</span></td>
                  <td className="px-4 py-2 text-[#7B6157]">{fmt(d.date_diagnosed || d.onset_date)}</td>
                  <td className="px-4 py-2 text-[#7B6157]">{d.updated_at ? fmt(d.updated_at) : "-"}</td>
                  <td className="px-4 py-2 text-[#7B6157]" data-testid={`problem-ruled-out-${d.id}`}>{fmt(d.ruled_out_date)}</td>
                  <td className="px-4 py-2 text-[#5D4037]" data-testid={`problem-by-${d.id}`}>{d.diagnosed_by || d.created_by_name || "-"}</td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end gap-1">
                      {sug && editable && (
                        <IconBtn testId={`problem-schedule-${d.id}`} label="Schedule follow-up"
                          onClick={() => scheduleFollowup(d, sug)}>
                          <CalendarPlus size={13} />
                        </IconBtn>
                      )}
                      {d.status !== "Voided" && <IconBtn testId={`problem-education-${d.id}`} label="Patient Education" onClick={() => setEducation(d)}>
                        <GraduationCap size={13} />
                      </IconBtn>}
                      {d.status !== "Voided" && <IconBtn testId={`problem-hl7-${d.id}`} label="Generate HL7" onClick={() => openHl7(d)}>
                        <FileCode2 size={13} />
                      </IconBtn>}
                      {editable && !["Archived", "Voided"].includes(d.status) && <IconBtn testId={`edit-problem-${d.id}`} label="Edit" onClick={() => { setForm({ ...DX_EMPTY, ...d }); setEditId(d.id); setPanel(true); }} />}
                      {editable && !["Archived", "Voided"].includes(d.status) && canRemoveDx(user, d) && <IconBtn testId={`delete-problem-${d.id}`} label="Archive" onClick={() => setDel(d)}><Archive size={13} /></IconBtn>}
                      {editable && d.status !== "Voided" && canRemoveDx(user, d) && <IconBtn testId={`void-problem-${d.id}`} label="Void, entered in error" danger onClick={() => setVoiding(d)}><Ban size={13} /></IconBtn>}
                      {editable && d.status === "Archived" && canRemoveDx(user, d) && <IconBtn testId={`restore-problem-${d.id}`} label="Restore" onClick={() => restore(d)}><RotateCcw size={13} /></IconBtn>}
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Master Problem" : "Add New Master Problem"} testId="problem-slide-panel">
        <form onSubmit={submit} className="space-y-4 p-1">
          {codesLocked ? (
            <p className="rounded-xl bg-[#F5F2EB] px-3 py-2 text-xs text-[#6D4C41]" data-testid="problem-codes-locked">Only {form.created_by_name || form.diagnosed_by || "the clinician who added it"} or an admin can change the code or description. You can still update the status, dates, and notes.</p>
          ) : (
          <div className="rounded-xl border border-[#EFEBE0] bg-[#F9F5EE] p-3">
            <FLabel><span className="inline-flex items-center gap-1.5">Find in Diagnosis Library <NewPill feature="diagnosis-library" /></span></FLabel>
            <DiagnosisPicker testId="problem-dx-picker" onPick={(row) => setForm((s) => ({ ...s, catalog_id: row.id, icd10_code: row.code, code: row.code, description: row.title, icd9_code: row.icd9 || "", snomed_code: row.snomed || "" }))} />
            {form.catalog_id && <p className="mt-1.5 text-[11px] text-[#2E7D32]" data-testid="problem-dx-linked">Linked to library entry {form.icd10_code}</p>}
          </div>
          )}
          <fieldset disabled={codesLocked} className="space-y-4 disabled:opacity-70">
          <div><FLabel>Description *</FLabel><FInput data-testid="problem-desc" value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value, catalog_id: "" }))} placeholder="e.g., Major depressive disorder" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <FLabel>ICD-10</FLabel>
              <CodeAutocomplete kind="icd10" testId="problem-icd10" value={form.icd10_code}
                onChange={(v) => setForm((s) => ({ ...s, icd10_code: v, catalog_id: "" }))}
                onPick={(item) => setForm((s) => ({ ...s, icd10_code: item.code,
                                                    description: s.description || item.description }))}
                placeholder="F32.1 or depression" />
            </div>
            <div><FLabel>ICD-9</FLabel><FInput data-testid="problem-icd9" value={form.icd9_code} onChange={f("icd9_code")} placeholder="296.22" /></div>
            <div>
              <FLabel>SNOMED</FLabel>
              <CodeAutocomplete kind="snomed" testId="problem-snomed" value={form.snomed_code}
                onChange={(v) => setForm((s) => ({ ...s, snomed_code: v }))}
                onPick={(item) => setForm((s) => ({ ...s, snomed_code: item.code }))}
                placeholder="Search SNOMED…" />
            </div>
          </div>
          </fieldset>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Type</FLabel><FSelect data-testid="problem-type" value={form.dx_type} onChange={f("dx_type")}>{DX_TYPES.map((t) => <option key={t} value={t}>{DX_TYPE_LABELS[t]}</option>)}</FSelect>
              {DX_TYPE_HINT[form.dx_type] && <p className="mt-1 text-[11px] text-[#7B6157]" data-testid="problem-type-hint">{DX_TYPE_HINT[form.dx_type]}</p>}
              <PrincipalNote items={items} form={form} editId={editId} testId="problem-principal-note" /></div>
            <div><FLabel>Status</FLabel><FSelect data-testid="problem-status" value={form.status} onChange={f("status")}>{DX_STATUSES.map((s) => <option key={s}>{s}</option>)}</FSelect></div>
            <div><FLabel>Severity</FLabel><FSelect data-testid="problem-severity" value={form.severity} placeholder="Not specified" onChange={f("severity")}>
              {["Mild", "Moderate", "Severe", "Life-threatening"].map((s) => <option key={s} value={s}>{s}</option>)}
            </FSelect></div>
            <div><FLabel>Chronicity</FLabel><FSelect data-testid="problem-chronicity" value={form.chronicity} placeholder="Not specified" onChange={f("chronicity")}>
              {["Acute", "Subacute", "Chronic", "Episodic", "Recurrent", "In Remission"].map((s) => <option key={s} value={s}>{s}</option>)}
            </FSelect></div>
            <div><FLabel>Certainty</FLabel><FSelect data-testid="problem-certainty" value={form.certainty} onChange={f("certainty")}>
              {["Confirmed", "Provisional", "Rule Out", "Differential", "Suspected"].map((s) => <option key={s}>{s}</option>)}
            </FSelect></div>
            <div><FLabel>Body Site</FLabel><FInput data-testid="problem-body-site" value={form.body_site} onChange={f("body_site")} placeholder="e.g., left knee, thoracic spine" /></div>
            <div><FLabel>Onset Date</FLabel><DateField testId="problem-onset" value={form.onset_date} onChange={(v) => setForm((s) => ({ ...s, onset_date: v }))} /></div>
            <div><FLabel>Date Diagnosed</FLabel><DateField testId="problem-diagnosed" value={form.date_diagnosed} onChange={(v) => setForm((s) => ({ ...s, date_diagnosed: v }))} /></div>
            <div><FLabel>Resolution Date</FLabel><DateField testId="problem-resolution" value={form.resolution_date} onChange={(v) => setForm((s) => ({ ...s, resolution_date: v }))} /></div>
            {form.dx_type === "Rule-out" && <div><FLabel>Date Ruled Out</FLabel><DateField testId="problem-ruled-out" value={form.ruled_out_date} onChange={(v) => setForm((s) => ({ ...s, ruled_out_date: v }))} />
              <p className="mt-1 text-[10px] text-[#84675B]">Leave blank to use today.</p></div>}
            <div><FLabel>Diagnosing Clinician</FLabel><ClinicianSelect testId="problem-by" value={form.diagnosed_by} valueId={form.diagnosed_by_id}
              onChange={({ name, id }) => setForm((s) => ({ ...s, diagnosed_by: name, diagnosed_by_id: id }))} /></div>
            <div><FLabel>Verified By (co-signer)</FLabel><FInput data-testid="problem-verifier" value={form.verifier} onChange={f("verifier")} /></div>
          </div>
          <div><FLabel>Symptoms</FLabel><FTextarea data-testid="problem-symptoms" rows={2} value={form.symptoms} onChange={f("symptoms")} placeholder="Comma-separated list of active symptoms" /></div>
          <div><FLabel>Triggers</FLabel><FTextarea data-testid="problem-triggers" rows={2} value={form.triggers} onChange={f("triggers")} placeholder="Known triggers or exacerbating factors" /></div>
          <div><FLabel>Notes / Specifiers</FLabel><FTextarea data-testid="problem-notes" rows={3} value={form.notes} onChange={f("notes")} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="problem-save" type="submit">{editId ? "Save Changes" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ArchiveDiagnosisDialog dx={del} onClose={() => setDel(null)} onDone={() => { setDel(null); changed(); }} />
      <VoidDiagnosisDialog dx={voiding} onClose={() => setVoiding(null)} onDone={() => { setVoiding(null); changed(); }} />
      <DiagnosisHistoryPanel client={client} open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <PatientEducationPanel dx={education} client={client} onClose={() => setEducation(null)} />

      <SlidePanel open={!!hl7} onClose={() => setHl7(null)} title="HL7 Public Health Surveillance" testId="hl7-panel">
        {hl7 && (
          <div className="space-y-3 p-1" data-testid="hl7-content">
            <div className="rounded-xl bg-[#FDFCF8] border border-[#EFEBE0] p-3 text-xs text-[#7B6157]">
              Generated as an HL7 v2.5.1 ORU^R01 message for the state health department. Copy and route through your integration engine before transmission.
              <div className="mt-1 font-mono text-[10px]">Message ID: {hl7.message_id}</div>
            </div>
            <pre className="whitespace-pre-wrap break-all rounded-xl bg-[#3E2723] p-3 font-mono text-xs text-[#F5F2EB]" data-testid="hl7-message">{hl7.message}</pre>
            <div className="flex flex-wrap justify-end gap-2">
              <Btn variant="outline" data-testid="hl7-export-csv" onClick={() => exportDx(hl7.dx, "csv")}><Download size={13} /> Download CSV</Btn>
              <Btn variant="outline" data-testid="hl7-export-pdf" onClick={() => exportDx(hl7.dx, "pdf")}><FileText size={13} /> Download PDF</Btn>
              <Btn variant="outline" data-testid="hl7-copy" onClick={() => { navigator.clipboard.writeText(hl7.message); toast.success("HL7 message copied to clipboard"); }}>
                <Copy size={13} /> Copy Message
              </Btn>
            </div>
            <p className="text-[11px] text-[#7B6157]">Downloads contain protected health information. Send only through an approved public health channel. Clinic-wide exports are in Reports, Public Health.</p>
          </div>
        )}
      </SlidePanel>

      <SlidePanel open={!!detailFollow} onClose={() => setDetailFollow(null)}
        title="Suggested Follow-Up" testId="followup-panel">
        {detailFollow && (
          <div className="space-y-4 p-1" data-testid="followup-content">
            <div className="rounded-2xl bg-[#F5F2EB] p-4">
              <div className="text-[10px] font-bold uppercase text-[#7B6157]">Problem</div>
              <div className="font-heading text-lg font-bold text-[#3E2723]">{detailFollow.dx.description}</div>
              <div className="text-xs text-[#7B6157] font-mono">{detailFollow.dx.icd10_code || detailFollow.dx.code}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-[#7B6157]">Recommendation</div>
              <div className="font-heading text-base font-bold text-[#3E2723]">{detailFollow.sug.label}</div>
              <div className="mt-1 text-xs text-[#7B6157]">
                Suggested date: <span className="font-semibold text-[#4E342E]">{dayjs(detailFollow.sug.suggested_date).format("dddd, MMM D, YYYY")}</span>
              </div>
              <div className="text-xs text-[#7B6157]">Visit type: <span className="font-semibold text-[#4E342E]">{detailFollow.sug.visit_type}</span></div>
            </div>
            <div className="rounded-xl border border-[#EFEBE0] bg-white p-3">
              <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase text-[#7B6157]"><Info size={11} /> Rationale</div>
              <p className="text-sm text-[#5D4037]">{detailFollow.sug.rationale}</p>
              {detailFollow.sug.reason === "lithium_on_board" && (
                <p className="mt-2 text-xs text-[#8D5A00]">Lithium is on this client&apos;s active medication list, so the standard interval is shortened and TSH plus lithium level should be part of the visit.</p>
              )}
            </div>
            {editable && (
              <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
                <Btn variant="ghost" onClick={() => setDetailFollow(null)}>Close</Btn>
                <Btn data-testid="followup-schedule-btn"
                  onClick={() => { scheduleFollowup(detailFollow.dx, detailFollow.sug); setDetailFollow(null); }}>
                  <CalendarPlus size={13} /> Schedule follow-up
                </Btn>
              </div>
            )}
          </div>
        )}
      </SlidePanel>
    </div>
  );
}

export default ProblemList;
